<template>
    <div v-if="record" class="human-body">
        <section v-for="field in RECORD_FIELDS" :key="field" class="human-body__section">
            <div class="human-body__label">
                <span>{{ fieldMeta[field]?.label ?? field }}</span>
            </div>
            <FormatValue
                class="human-body__value"
                :record="record"
                :field-key="field"
                :field-definitions="fieldDefinitions"
                :field-meta="fieldMeta"
            />
        </section>

        <section class="human-body__section">
            <div class="human-body__label">
                <span>Logins</span>
                <small>Each way this person signs in. A login identifies and nothing more.</small>
            </div>
            <ul class="human-body__list" data-list="logins">
                <li v-for="login in record.logins" :key="`${login.provider}:${login.email}`" class="human-body__row">
                    <i :class="`pi ${providerIcon(login.provider)}`" />
                    <div class="human-body__row-body">
                        <span>{{ providerLabel(login.provider) }}</span>
                        <small v-if="login.email">{{ login.email }}</small>
                        <small v-if="login.connectedAt">Connected {{ when(login.connectedAt) }}</small>
                    </div>
                    <Tag v-if="login.dormantAt" value="Dormant" severity="secondary" />
                </li>
            </ul>
            <p v-if="record.logins.length === 0" class="human-body__empty">No logins.</p>
        </section>

        <section class="human-body__section">
            <div class="human-body__label">
                <span>Devices</span>
                <small>A device is a browser profile holding a key; every tab of it shares one session.</small>
            </div>
            <ProgressSpinner v-if="detail === null && store.loading" class="human-body__loading" style="width: 24px; height: 24px" />
            <p v-else-if="detail === null && loadFailure" class="human-body__failure">{{ loadFailure }}</p>
            <template v-else-if="detail">
                <ul class="human-body__list" data-list="devices">
                    <li v-for="device in detail.devices" :key="device.thumbprint" class="human-body__row">
                        <i class="pi pi-key" />
                        <div class="human-body__row-body">
                            <code>{{ device.thumbprint }}</code>
                            <small v-if="device.registeredAt">Registered {{ when(device.registeredAt) }}</small>
                        </div>
                    </li>
                </ul>
                <p v-if="detail.devices.length === 0" class="human-body__empty">No devices.</p>
            </template>
        </section>

        <section v-if="detail" class="human-body__section">
            <div class="human-body__label">
                <span>Sessions</span>
                <small>One row per device: the app, the browser on its system, how it signed in, and where.</small>
            </div>
            <ul class="human-body__list" data-list="sessions">
                <li v-for="row in sessionRows" :key="row.id" class="human-body__row" :data-session="row.id">
                    <i :class="`pi ${row.icon}`" />
                    <div class="human-body__row-body">
                        <span>
                            {{ row.appName }}
                            <span v-if="row.isThisDevice" class="human-body__this-device" data-this-device>This device</span>
                        </span>
                        <small>{{ row.device }}</small>
                        <small v-for="line in row.details" :key="line">{{ line }}</small>
                    </div>
                    <Button
                        label="Sign out this device"
                        icon="pi pi-sign-out"
                        severity="secondary"
                        variant="outlined"
                        size="small"
                        :disabled="busy"
                        data-action="sign-out-device"
                        @click="askSignOutDevice(row)"
                    />
                </li>
            </ul>
            <p v-if="sessionRows.length === 0" class="human-body__empty">No active sessions.</p>
            <Button
                label="Sign out everywhere"
                icon="pi pi-sign-out"
                severity="danger"
                variant="outlined"
                size="small"
                class="human-body__everywhere"
                :disabled="busy || sessionRows.length === 0"
                data-action="sign-out-everywhere"
                @click="askSignOutEverywhere"
            />
        </section>

        <section v-if="record.status !== 'retired'" class="human-body__section">
            <div class="human-body__label">
                <span>Account</span>
                <small>Closing is what the person does from Sign-in &amp; security. Nothing is deleted: the person restores the account by signing in again with any old login.</small>
            </div>
            <Button
                label="Close account"
                icon="pi pi-times-circle"
                severity="danger"
                variant="outlined"
                size="small"
                class="human-body__close"
                :disabled="busy"
                data-action="close-account"
                @click="askClose"
            />
        </section>

        <ActionConfirmDialog
            v-model:open="confirm.open"
            :title="confirm.title"
            :message="confirm.message"
            :confirm-label="confirm.confirmLabel"
            destructive
            :busy="busy"
            :error="confirm.error"
            @confirm="runConfirmed"
        />
    </div>
</template>

<script setup>
import { describeDevice } from '@hds/identity-client-sdk'
import Button from 'primevue/button'
import ProgressSpinner from 'primevue/progressspinner'
import Tag from 'primevue/tag'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { FormatValue } from '@/admin-collection-layout/lib/formatValue.js'
import ActionConfirmDialog from '@/components/identity/ActionConfirmDialog.vue'
import { providerIcon, providerLabel } from '@/lib/identityCollections.js'

defineOptions({ name: 'HumanInstanceBody' })

// What the instance panel shows for a Human: who they are, their logins, their devices and
// their sessions, with a way to sign one device out or all of them. The list row carries the
// record and the logins; the devices and sessions come from reading the one person, which
// this body does as soon as it is handed a record.
const props = defineProps({
    // The list row: `{ id, name, primaryEmail, status, closedAt, logins, deviceCount }`.
    record: { type: Object, default: null },
    // `humansStore()`.
    store: { type: Object, required: true },
    fieldDefinitions: { type: Object, required: true },
    fieldMeta: { type: Object, default: () => ({}) }
})

const RECORD_FIELDS = ['name', 'primaryEmail', 'status', 'closedAt']

const { t, locale } = useI18n()

const busy = ref(false)
const confirm = ref({ open: false, title: '', message: '', confirmLabel: '', error: '', action: null })
const loadFailure = ref(null)

// Which person the held detail is about: `store.current` outlives the record it was read for.
const detail = computed(function () {
    if (props.store.current === null || props.store.current.id !== props.record?.id) {
        return null
    }
    return props.store.current
})

async function loadDetail() {
    loadFailure.value = null
    if (props.record === null) {
        return
    }
    const ok = await props.store.load(props.record.id)
    if (ok === false) {
        loadFailure.value = props.store.error
    }
}

watch(() => props.record?.id, loadDetail, { immediate: true })

function when(iso) {
    return new Date(iso).toLocaleString(locale.value, { dateStyle: 'medium', timeStyle: 'short' })
}

function iconFor(system) {
    if (system === 'iPhone' || system === 'Android') {
        return 'pi-mobile'
    }
    if (system === 'iPad') {
        return 'pi-tablet'
    }
    return 'pi-desktop'
}

// What a session row says, in the words Sign-in & security uses for the person's own view.
// Read only under the template's `v-if="detail"`, so the detail is there when this runs.
const sessionRows = computed(() => detail.value.sessions.map(function (row) {
    const { browser, system } = describeDevice(row.userAgent)
    const details = []
    if (row.signInMethod) {
        details.push(`Signed in with ${providerLabel(row.signInMethod)}`)
    }
    if (row.ipAddress) {
        details.push(`IP address ${row.ipAddress}`)
    }
    details.push(`Signed in ${when(row.signedInAt)}`)
    details.push(`Last active ${when(row.lastActiveAt)}`)
    return {
        id: row.id,
        // The administrator's own device, when they are looking at themselves: the server marks
        // it and lists it first, as it does on Sign-in & security.
        isThisDevice: row.isThisDevice === true,
        icon: iconFor(system),
        appName: t(`authentication.sessions.apps.${row.appId}`, row.appId),
        device: t('authentication.sessions.browserOnSystem', {
            browser: browser ?? t('authentication.sessions.unknownBrowser'),
            system: system ?? t('authentication.sessions.unknownSystem')
        }),
        details
    }
}))

function askSignOutDevice(row) {
    confirm.value = {
        open: true,
        title: 'Sign out this device?',
        message: `${row.appName} on ${row.device} is signed out at once and must sign in again. The person's other devices stay signed in.`,
        confirmLabel: 'Sign out this device',
        error: '',
        action: () => props.store.endSessions(props.record.id, row.id)
    }
}

function askSignOutEverywhere() {
    confirm.value = {
        open: true,
        title: 'Sign out everywhere?',
        message: `${props.record.name || 'This person'} is signed out of every device at once and must sign in again on each one.`,
        confirmLabel: 'Sign out everywhere',
        error: '',
        action: () => props.store.endSessions(props.record.id)
    }
}

// The same close the person makes from Sign-in & security (the account lifecycle's "retire,
// resurrectable"): nothing is deleted, and any old login brings the account back.
function askClose() {
    confirm.value = {
        open: true,
        title: `Close ${props.record.name || 'this person'}'s account?`,
        message: `The account is closed: its contact details are stripped from our records, every login goes dormant, and every session ends at once. ${props.record.name || 'The person'} can restore it by signing in again with any of their old logins.`,
        confirmLabel: 'Close account',
        error: '',
        action: () => props.store.close(props.record.id)
    }
}

// A failure answers inside the open confirm, and confirming again retries. Success closes it
// and re-reads the page and the person, so the rows, the device count and the status are live.
async function runConfirmed() {
    busy.value = true
    confirm.value.error = ''
    const ok = await confirm.value.action()
    if (ok === false) {
        confirm.value.error = props.store.error
        busy.value = false
        return
    }
    await props.store.refresh()
    confirm.value.open = false
    busy.value = false
}
</script>

<style scoped>
.human-body {
    display: flex;
    flex-direction: column;
}

.human-body__section {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 10px 0;
}

.human-body__label {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.human-body__label span {
    font-size: 13px;
    font-weight: 600;
}

.human-body__label small {
    color: var(--p-surface-400);
    font-size: 11px;
    line-height: 1.4;
}

.human-body__value {
    font-size: 13px;
}

.human-body__list {
    list-style: none;
    margin: 4px 0 0;
    padding: 0;
}

.human-body__row {
    display: flex;
    align-items: center;
    gap: 10px;
    border-bottom: 1px solid var(--p-content-border-color);
    font-size: 13px;
    padding: 8px 0;
}

.human-body__row > i {
    color: var(--p-primary-color);
    font-size: 14px;
}

.human-body__row-body {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
}

.human-body__row-body small,
.human-body__row-body code {
    color: var(--p-surface-400);
    font-size: 11px;
    overflow-wrap: anywhere;
}

.human-body__row-body code {
    font-family: var(--p-font-mono);
    color: var(--p-text-color);
}

.human-body__empty {
    color: var(--p-surface-400);
    font-size: 12px;
    margin: 0;
}

.human-body__failure {
    color: var(--p-status-danger);
    font-size: 12px;
    margin: 0;
}

.human-body__loading {
    align-self: flex-start;
}

.human-body__everywhere {
    align-self: flex-start;
    margin-top: 8px;
}

.human-body__this-device {
    margin-left: 6px;
    padding: 2px 8px;
    border-radius: 999px;
    background: var(--p-primary-color);
    color: var(--p-primary-contrast-color);
    font-size: 11px;
    font-weight: 600;
    vertical-align: middle;
}
</style>
